import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { addQuarters } from 'date-fns/addQuarters'
import { addWeeks } from 'date-fns/addWeeks'
import { addYears } from 'date-fns/addYears'
import { differenceInDays } from 'date-fns/differenceInDays'
import { formatISO } from 'date-fns/formatISO'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { getUnixTime } from 'date-fns/getUnixTime'
import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { isSameDay } from 'date-fns/isSameDay'
import { lastDayOfMonth } from 'date-fns/lastDayOfMonth'
import { nextDay } from 'date-fns/nextDay'
import { parseISO } from 'date-fns/parseISO'
import { setDate } from 'date-fns/setDate'
import { setDay } from 'date-fns/setDay'
import { setDayOfYear } from 'date-fns/setDayOfYear'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'
import { subMonths } from 'date-fns/subMonths'
import { subQuarters } from 'date-fns/subQuarters'
import { subWeeks } from 'date-fns/subWeeks'
import { subYears } from 'date-fns/subYears'

import type { Day } from 'date-fns'

import { DATE_FORMATS_INTL, formatDate } from '../constants'
import {
  ALLOWED_MERCHANTLESS_BILL_CATEGORIES,
  ALLOWED_MERCHANTLESS_INCOME_MAIN_CLASSES,
} from '../constants/repeatingTransactions'
import {
  type Account,
  type DateRange,
  type Recurrence,
  type RecurringCopy,
  type RepeatingTransaction,
  type Transaction,
  RecurrenceType,
  RecurrenceStatus,
  RepeatingType,
  Frequency,
} from '../types'

import {
  getNextBiMonthlyDate,
  getNextBiQuarterlyDate,
  getNextBiWeeklyDate,
  getNextMonthlyDate,
  getNextQuarterlyDate,
  getNextTwiceMonthlyDate,
  getNextWeeklyDate,
  getNextYearlyDate,
  getTwiceMonthlyDates,
} from './DateUtil'
import { formatOrdinal } from './NumberFormatting'
import { buildDynamicCopy } from './Localization'

export type GroupedRecurrence = {
  recurrences: Recurrence[]
  id: string
  date: Date
}

export const buildInstitutionName = (account?: Account) => {
  if (!account) return ''

  let name = account.user_name || account.feed_name || ''
  if (account.account_number) {
    name = `${name} *${account.account_number}`
  }
  return name
}

const buildRepeatingRecurrences = (
  repeatingTransaction: RepeatingTransaction,
  dateRange: DateRange,
) => {
  const recurrences: Recurrence[] = []

  const isYearly = repeatingTransaction.recurrence_type === Frequency.EveryYear

  const pastTransaction = repeatingTransaction.transactions.find(
    (t) => t.repeating_transaction_guid === repeatingTransaction.guid,
  )

  /*
  Since past transactions for yearly recurrences can be outside the 90 day data range, we need to display them
  even if there is no past transaction to match to. This will display it without a category, but that's the
  tradeoff until we impove our transactions service to return past transactions for all recurring transactions.
  */
  if (!isYearly && !pastTransaction) return []

  const occursOn = parseISO(repeatingTransaction.predicted_occurs_on)
  const baseRecurrence: Recurrence = {
    accountGuid: repeatingTransaction.account?.guid || '',
    amount: repeatingTransaction.amount,
    categoryGuid: pastTransaction?.category_guid || '',
    expectedDate: occursOn,
    frequency: repeatingTransaction.recurrence_type,
    institutionGuid: repeatingTransaction.account?.institution_guid || '',
    institutionName: buildInstitutionName(repeatingTransaction.account),
    merchantGuid: repeatingTransaction.merchant_guid,
    name: repeatingTransaction.description,
    recurrenceDay: repeatingTransaction.recurrence_day,
    repeatingTransactionGuid: repeatingTransaction.guid,
    secondRecurrenceDay: repeatingTransaction.second_recurrence_day,
    status: RecurrenceStatus.Upcoming,
    type:
      repeatingTransaction.repeating_transaction_type === RepeatingType.Income
        ? RecurrenceType.Income
        : RecurrenceType.Expense,
    transaction: undefined,
  }

  const startDate = dateRange.start
  const endDate = dateRange.end

  switch (repeatingTransaction.recurrence_type) {
    case Frequency.EveryWeek:
      recurrences.push(...getWeeklyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.EveryOtherWeek:
      recurrences.push(...getBiWeeklyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.TwiceAMonth:
      recurrences.push(...getTwiceMonthlyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.EveryMonth:
      recurrences.push(...getMonthlyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.EveryOtherMonth:
      recurrences.push(...getBiMonthlyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.EveryQuarter:
      recurrences.push(...getQuarterlyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.EveryOtherQuarter:
      recurrences.push(...getBiQuarterlyRecurrences(baseRecurrence, startDate, endDate))
      break
    case Frequency.EveryYear:
      recurrences.push(...getYearlyRecurrences(baseRecurrence, startDate, endDate))
      break
  }

  // Track which transactions have already been matched to avoid double-counting
  const matchedTransactionGuids = new Set<string>()

  for (const recurrence of recurrences) {
    // First, try to find a manually linked transaction (any date)
    const linkedTransactions = repeatingTransaction.transactions.filter(
      (t) =>
        t.repeating_transaction_guid === repeatingTransaction.guid &&
        !matchedTransactionGuids.has(t.guid),
    )

    const transaction =
      linkedTransactions.find(
        (t) =>
          t.date > getUnixTime(subDays(recurrence.expectedDate, 5)) &&
          t.date < getUnixTime(addDays(recurrence.expectedDate, 5)),
      ) ||
      (isBefore(recurrence.expectedDate, startOfToday())
        ? linkedTransactions.reduce<Transaction | undefined>((closest, t) => {
            const dist = Math.abs(t.date - getUnixTime(recurrence.expectedDate))
            const closestDist = closest
              ? Math.abs(closest.date - getUnixTime(recurrence.expectedDate))
              : Infinity
            return dist < closestDist ? t : closest
          }, undefined)
        : undefined)

    if (transaction) {
      matchedTransactionGuids.add(transaction.guid)
      recurrence.transaction = transaction
      recurrence.status = RecurrenceStatus.Paid
      recurrence.occurredOnDate = fromUnixTime(transaction.date)
    } else if (isBefore(recurrence.expectedDate, subDays(startOfToday(), 3))) {
      recurrence.status = RecurrenceStatus.Missed
    }
  }

  return recurrences
}

const getWeeklyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const activities: Recurrence[] = []

  // Add recurrence for each week for the current month and next month
  let nextDate = nextDay(startDate, baseRecurrence.recurrenceDay as Day)
  while (isBefore(nextDate, endDate)) {
    activities.push({ ...baseRecurrence, expectedDate: nextDate })
    nextDate = nextDay(nextDate, baseRecurrence.recurrenceDay as Day)
  }

  return activities
}

const getBiWeeklyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  // Determine the first occurrence of the bi-weekly recurrence within the date range
  let nextDate = baseRecurrence.expectedDate
  if (isBefore(nextDate, startDate)) {
    while (isBefore(nextDate, startDate)) {
      nextDate = addWeeks(nextDate, 2)
    }
  } else {
    while (isAfter(nextDate, startDate) || isSameDay(nextDate, startDate)) {
      nextDate = subWeeks(nextDate, 2)
    }
    nextDate = addWeeks(nextDate, 2)
  }

  while (isBefore(nextDate, endDate)) {
    recurrences.push({ ...baseRecurrence, expectedDate: nextDate })
    nextDate = addDays(nextDate, 14)
  }

  return recurrences
}

const getTwiceMonthlyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  let firstOccurrence = setDate(startDate, baseRecurrence.recurrenceDay)

  while (isBefore(firstOccurrence, endDate)) {
    recurrences.push({ ...baseRecurrence, expectedDate: firstOccurrence })
    firstOccurrence = addMonths(firstOccurrence, 1)
  }

  let secondOccurrence =
    baseRecurrence.secondRecurrenceDay && baseRecurrence.secondRecurrenceDay < 28
      ? setDate(startDate, baseRecurrence.secondRecurrenceDay)
      : lastDayOfMonth(startDate)

  while (isBefore(secondOccurrence, endDate)) {
    recurrences.push({ ...baseRecurrence, expectedDate: secondOccurrence })
    secondOccurrence = addMonths(secondOccurrence, 1)
  }

  return recurrences
}

const getMonthlyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  let expectedDate = setDate(startDate, baseRecurrence.recurrenceDay)

  // Add current month's repeating transaction recurrence
  recurrences.push({ ...baseRecurrence, expectedDate })

  expectedDate = addMonths(expectedDate, 1)
  while (isBefore(expectedDate, endDate)) {
    recurrences.push({ ...baseRecurrence, expectedDate })
    expectedDate = addMonths(expectedDate, 1)
  }

  return recurrences
}

const getBiMonthlyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  const occursOn = baseRecurrence.expectedDate
  let nextDate = isBefore(occursOn, endDate) ? occursOn : subMonths(occursOn, 2)
  while (isBefore(nextDate, endDate)) {
    if (isAfter(nextDate, startDate)) {
      recurrences.push({ ...baseRecurrence, expectedDate: nextDate })
    }
    nextDate = addMonths(nextDate, 2)
  }

  return recurrences
}

const getQuarterlyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  const occursOn = baseRecurrence.expectedDate
  let nextDate = isBefore(occursOn, endDate) ? occursOn : subQuarters(occursOn, 1)
  while (isBefore(nextDate, endDate)) {
    if (isAfter(nextDate, startDate)) {
      recurrences.push({ ...baseRecurrence, expectedDate: nextDate })
    }
    nextDate = addQuarters(nextDate, 1)
  }

  return recurrences
}

const getBiQuarterlyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  const occursOn = baseRecurrence.expectedDate
  let nextDate = isBefore(occursOn, endDate) ? occursOn : subQuarters(occursOn, 2)
  while (isBefore(nextDate, endDate)) {
    if (isAfter(nextDate, startDate)) {
      recurrences.push({ ...baseRecurrence, expectedDate: nextDate })
    }
    nextDate = addQuarters(nextDate, 2)
  }

  return recurrences
}

const getYearlyRecurrences = (baseRecurrence: Recurrence, startDate: Date, endDate: Date) => {
  const recurrences: Recurrence[] = []

  const occursOn = baseRecurrence.expectedDate
  let nextDate = isBefore(occursOn, endDate) ? occursOn : subYears(occursOn, 1)
  while (isBefore(nextDate, endDate)) {
    if (isAfter(nextDate, startDate)) {
      recurrences.push({ ...baseRecurrence, expectedDate: nextDate })
    }
    nextDate = addYears(nextDate, 1)
  }

  return recurrences
}

export const buildAllRecurrences = (
  repeatingTransactions: RepeatingTransaction[],
  dateRange: DateRange,
) => {
  const recurrences: Recurrence[] = []

  for (const repeatingTransaction of repeatingTransactions) {
    const activity = buildRepeatingRecurrences(repeatingTransaction, dateRange)
    recurrences.push(...activity)
  }

  return recurrences.sort(
    (a, b) =>
      getUnixTime(a.occurredOnDate || a.expectedDate) -
      getUnixTime(b.occurredOnDate || b.expectedDate),
  )
}

export const augmentRepeatingTransactions = (
  accounts: Account[],
  repeatingTransactions: RepeatingTransaction[],
  transactions: Transaction[],
) => {
  const augmentedRepeatingTransactions: RepeatingTransaction[] = []

  for (const repeatingTransaction of repeatingTransactions) {
    const relatedTransactions = transactions.filter(
      (t) => t.repeating_transaction_guid === repeatingTransaction.guid,
    )

    if (transactions.length > 0) {
      augmentedRepeatingTransactions.push({
        ...repeatingTransaction,
        account: accounts.find((account) => account.guid === repeatingTransaction.account_guid),
        transactions: relatedTransactions,
      })
    }
  }

  return augmentedRepeatingTransactions
}

export const groupRecurrencesByDate = (recurrences: Recurrence[]): GroupedRecurrence[] => {
  return recurrences.reduce((groupedRecurrences: GroupedRecurrence[], recurrence) => {
    const dateString = formatISO(recurrence.expectedDate, { representation: 'date' })
    const existingGroup = groupedRecurrences.find(
      (group) => group.date.toString() === recurrence.expectedDate.toString(),
    )

    if (existingGroup) {
      existingGroup.recurrences.push(recurrence)
    } else {
      groupedRecurrences.push({
        date: recurrence.expectedDate,
        id: `date-${dateString}`,
        recurrences: [recurrence],
      })
    }

    return groupedRecurrences
  }, [])
}

export const getFrequencyLabel = (
  copy: RecurringCopy,
  repeatingTransaction: RepeatingTransaction,
  showOrdinal: boolean,
) => {
  const nextWeekday = nextDay(startOfToday(), repeatingTransaction.recurrence_day as Day)
  const nextAnnualDate = setDayOfYear(startOfToday(), repeatingTransaction.recurrence_day)
  const firstDateOrdinal = formatOrdinal(repeatingTransaction.recurrence_day)
  const secondDateOrdinal = repeatingTransaction.second_recurrence_day
    ? formatOrdinal(repeatingTransaction.second_recurrence_day)
    : ''

  let frequencyLabel = showOrdinal
    ? buildDynamicCopy(copy.monthly_ordinal, firstDateOrdinal)
    : copy.monthly

  switch (repeatingTransaction.recurrence_type) {
    case Frequency.EveryQuarter:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(copy.quarterly_ordinal, firstDateOrdinal)
        : copy.quarterly
      break
    case Frequency.EveryWeek:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(
            copy.weekly_ordinal,
            formatDate(nextWeekday, DATE_FORMATS_INTL.DAY_OF_WEEK),
          )
        : copy.weekly
      break
    case Frequency.EveryYear:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(
            copy.anually_ordinal,
            formatDate(nextAnnualDate, DATE_FORMATS_INTL.MONTH_DAY),
          )
        : copy.anually
      break
    case Frequency.EveryOtherWeek:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(
            copy.every_other_week_ordinal,
            formatDate(nextWeekday, DATE_FORMATS_INTL.DAY_OF_WEEK),
          )
        : copy.every_other_week
      break
    case Frequency.EveryOtherMonth:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(copy.every_other_month_ordinal, firstDateOrdinal)
        : copy.every_other_month
      break
    case Frequency.EveryOtherQuarter:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(copy.every_other_quarter_ordinal, firstDateOrdinal)
        : copy.every_other_quarter
      break
    case Frequency.TwiceAMonth:
      frequencyLabel = showOrdinal
        ? buildDynamicCopy(copy.twice_a_month_ordinal, firstDateOrdinal, secondDateOrdinal)
        : copy.twice_a_month
      break
  }

  return frequencyLabel as string
}

export const getNextPaymentDate = (
  frequency: Frequency,
  date: Date,
  recurrenceDay?: number,
  secondRecurrenceDay?: number,
) => {
  let baseDate = date

  // Apply user selected recurrenceDay based on frequency type
  if (recurrenceDay !== undefined) {
    if (frequency === Frequency.EveryWeek || frequency === Frequency.EveryOtherWeek) {
      baseDate = setDay(date, recurrenceDay)
    } else if (frequency === Frequency.EveryYear) {
      baseDate = setDayOfYear(date, recurrenceDay)
    } else {
      baseDate = setDate(date, recurrenceDay)
    }
  }

  let nextDate = baseDate

  switch (frequency) {
    case Frequency.EveryWeek:
      nextDate = getNextWeeklyDate(baseDate)
      break
    case Frequency.EveryOtherWeek:
      nextDate = getNextBiWeeklyDate(baseDate)
      break
    case Frequency.TwiceAMonth:
      nextDate = getNextTwiceMonthlyDate(
        recurrenceDay ?? baseDate.getDate(),
        secondRecurrenceDay ?? 28,
      )
      break
    case Frequency.EveryMonth:
      nextDate = getNextMonthlyDate(baseDate)
      break
    case Frequency.EveryOtherMonth:
      nextDate = getNextBiMonthlyDate(baseDate)
      break
    case Frequency.EveryQuarter:
      nextDate = getNextQuarterlyDate(baseDate)
      break
    case Frequency.EveryOtherQuarter:
      nextDate = getNextBiQuarterlyDate(baseDate)
      break
    case Frequency.EveryYear:
      nextDate = getNextYearlyDate(baseDate)
      break
  }

  return nextDate
}

export const getNextUpcomingDate = (
  recurringTransaction: RepeatingTransaction,
  recurrences: Recurrence[],
): Date => {
  const nextUpcoming = recurrences.find(
    (r) =>
      r.repeatingTransactionGuid === recurringTransaction.guid &&
      r.status === RecurrenceStatus.Upcoming,
  )
  if (nextUpcoming) return nextUpcoming.expectedDate

  const isPaidNearDate = (date: Date) =>
    recurrences.some(
      (r) =>
        r.repeatingTransactionGuid === recurringTransaction.guid &&
        r.status === RecurrenceStatus.Paid &&
        Math.abs(differenceInDays(r.expectedDate, date)) <= 5,
    )

  const frequencyAdvancers: Partial<Record<Frequency, (d: Date) => Date>> = {
    [Frequency.EveryWeek]: (d) => addWeeks(d, 1),
    [Frequency.EveryOtherWeek]: (d) => addWeeks(d, 2),
    [Frequency.TwiceAMonth]: (d) => {
      const candidates = getTwiceMonthlyDates(
        recurringTransaction.recurrence_day,
        recurringTransaction.second_recurrence_day ?? 28,
      )
      return candidates.find((c) => isAfter(c, d)) ?? candidates[candidates.length - 1]
    },
    [Frequency.EveryMonth]: (d) => addMonths(d, 1),
    [Frequency.EveryOtherMonth]: (d) => addMonths(d, 2),
    [Frequency.EveryQuarter]: (d) => addMonths(d, 3),
    [Frequency.EveryOtherQuarter]: (d) => addMonths(d, 6),
    [Frequency.EveryYear]: (d) => addYears(d, 1),
  }

  const baseNext = getNextPaymentDate(
    recurringTransaction.recurrence_type,
    parseISO(recurringTransaction.predicted_occurs_on),
    recurringTransaction.recurrence_day,
    recurringTransaction.second_recurrence_day,
  )

  if (!isPaidNearDate(baseNext)) return baseNext

  const advance = frequencyAdvancers[recurringTransaction.recurrence_type]
  return advance ? advance(baseNext) : baseNext
}

export const isEligibleRepeatingTransaction = (transaction: Transaction): boolean => {
  if (transaction.merchant_guid && transaction.merchant_guid.trim() !== '') {
    return true
  }

  const categoryGuids = [transaction.category_guid, transaction.top_level_category_guid].filter(
    (guid): guid is string => !!guid,
  )
  if (
    categoryGuids.some((guid) =>
      (ALLOWED_MERCHANTLESS_BILL_CATEGORIES as readonly string[]).includes(guid),
    )
  ) {
    return true
  }

  if (
    transaction.main_class_guid &&
    (ALLOWED_MERCHANTLESS_INCOME_MAIN_CLASSES as readonly string[]).includes(
      transaction.main_class_guid,
    )
  ) {
    return true
  }

  return false
}
